<script setup>
import Layout from "../Layouts/Customer.vue";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import {ref, watch, onMounted, onBeforeUnmount} from "vue";
import {Inertia} from "@inertiajs/inertia";
import {useToast} from "primevue/usetoast";
import InputMask from "primevue/inputmask";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import {useForm} from "@inertiajs/inertia-vue3";
import Textarea from "primevue/textarea";

const props = defineProps([
    'documentation',
    'customer',
    'device',
    'menuItems',
    'flash',
]);
const toast = useToast()
const isEdit = ref(Boolean(props.documentation))
const submitting = ref(false)
const createEmptyRow = () => ({
    date: null,
    localization: null,
    applicators: null,
    time: null,
    remarks: null,
})
const form = useForm({
    customer_uuid: props.customer.uuid,
    device_uuid: props.device.uuid,
    documentation: isEdit.value ? props.documentation.documentation : [
        {
            date: null,
            localization: null,
            applicators: null,
            time: null,
            remarks: null,
        },
        {
            date: null,
            localization: null,
            applicators: null,
            time: null,
            remarks: null,
        },
        {
            date: null,
            localization: null,
            applicators: null,
            time: null,
            remarks: null,
        },
        {
            date: null,
            localization: null,
            applicators: null,
            time: null,
            remarks: null,
        },
    ],
})

// Form state tracking for unsaved changes
const initialFormHash = ref('');
const isFormDirty = ref(false);
const showNavigationDialog = ref(false);
const pendingNavigation = ref(null);

// Normalize form data to handle null/undefined/empty string consistently
function normalizeFormData(data) {
    return JSON.parse(JSON.stringify(data, (key, value) => {
        // Convert empty strings to null for consistent comparison
        if (value === '' || value === undefined) return null;
        return value;
    }));
}

// Hash function for form data comparison
async function hashFormData(data) {
    try {
        const normalizedData = normalizeFormData(data);
        const json = JSON.stringify(normalizedData);
        const msgBuffer = new TextEncoder().encode(json);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (e) {
        // Fallback to simple string comparison if crypto not available
        return JSON.stringify(normalizeFormData(data));
    }
}

// Track if we're allowing navigation
let allowNavigation = false;
let removeNavigationListener = null;

// Initialize hash on mount
onMounted(async () => {
    initialFormHash.value = await hashFormData(form.documentation);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Inertia navigation guard
    removeNavigationListener = Inertia.on('before', (event) => {
        if (!allowNavigation && isFormDirty.value) {
            event.preventDefault();
            // Store the URL to navigate to
            pendingNavigation.value = event.detail.visit.url;
            showNavigationDialog.value = true;
        }
    });
});

// Cleanup on unmount (registered at top level)
onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    if (removeNavigationListener) {
        removeNavigationListener();
    }
});

// Watch for form changes
watch(() => form.documentation, async (newData) => {
    const currentHash = await hashFormData(newData);
    isFormDirty.value = currentHash !== initialFormHash.value;
}, { deep: true });

// Browser navigation guard
const handleBeforeUnload = (e) => {
    // Don't show warning if we're allowing navigation (e.g., during save)
    if (!allowNavigation && isFormDirty.value) {
        e.preventDefault();
        e.returnValue = '';
        return '';
    }
};

// Dialog handlers
const confirmNavigation = () => {
    showNavigationDialog.value = false;
    if (pendingNavigation.value) {
        // Allow the next navigation attempt
        allowNavigation = true;
        // Navigate to the pending URL
        Inertia.visit(pendingNavigation.value);
        pendingNavigation.value = null;
    }
};

const cancelNavigation = () => {
    showNavigationDialog.value = false;
    pendingNavigation.value = null;
};

const submit = () => {
    submitting.value = true
    // Allow navigation during save
    allowNavigation = true

    const postUrl = isEdit.value
        ? `/documentations/${props.documentation.uuid}`
        : '/documentations'

    form.post(
        postUrl,
        {
            preserveState: false,

            onError: (errors) => {
                // Reset allowNavigation on error
                allowNavigation = false;
                toast.add({
                    severity: "error",
                    summary: "Error",
                    life: 5000,
                });
            },

            onSuccess: async (page) => {
                // Reset form state after successful save
                initialFormHash.value = await hashFormData(form.documentation);
                isFormDirty.value = false;
                
                toast.add({
                    severity: "success",
                    summary: "Aktualisiert",
                    life: 5000,
                });
            },

            onFinish: (visit) => {
                submitting.value = false;
                // Reset allowNavigation after save completes
                allowNavigation = false;
            }
        }
    )
}

const addMore = () => {
    form.documentation.push(createEmptyRow())
}

</script>
<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <!-- Navigation confirmation dialog -->
        <Dialog 
            v-model:visible="showNavigationDialog" 
            modal 
            :closable="false"
            :header="'Ungespeicherte Änderungen'"
            :style="{ width: '450px' }">
            
            <p>Sie haben ungespeicherte Änderungen. Möchten Sie die Seite wirklich verlassen?</p>
            
            <template #footer>
                <Button 
                    label="Abbrechen" 
                    @click="cancelNavigation" 
                    class="p-button-danger" />
                <Button 
                    label="Verlassen" 
                    @click="confirmNavigation" 
                    class="p-button-secondary" />
            </template>
        </Dialog>

        <div class="px-14 pt-7">
            <h3 class="font-bold mb-5">
                Nachsorge-Dokumentation für {{ device.name }}
            </h3>
        </div>

        <div class="grid grid-cols-9 gap-0 mb-5">
            <div class="bg-[#EEEBE4] h-full col-span-2">
                <div class="pl-14 pr-2 py-3">
                    <h4 class="text-black text-xs font-extralight">
                        Datum
                    </h4>
                </div>
            </div>

            <div class="bg-[#EEEBE4] h-full col-span-2">
                <div class="px-2 py-3">
                    <h4 class="text-black text-xs font-extralight">
                        Lokalisation
                    </h4>
                </div>
            </div>

            <div class="bg-[#EEEBE4] h-full">
                <div class="px-2 py-3">
                    <h4 class="text-black text-xs font-extralight">
                        Applicators
                    </h4>
                </div>
            </div>

            <div class="bg-[#EEEBE4] h-full">
                <div class="px-2 py-3">
                    <h4 class="text-black text-xs font-extralight">
                        Zeit
                    </h4>
                </div>
            </div>

            <div class="bg-[#EEEBE4] h-full">
                <div class="px-2 py-3">
                    <h4 class="text-black text-xs font-extralight">
                        Umfang
                    </h4>
                </div>
            </div>

            <div class="bg-[#EEEBE4] h-full">
                <div class="px-2 py-3">
                    <h4 class="text-black text-xs font-extralight">
                        Zange R
                    </h4>
                </div>
            </div>

            <div class="bg-[#EEEBE4] h-full">
                <div class="pl-2 pr-14 py-3">
                    <h4 class="text-black text-xs font-extralight">
                        Zange L
                    </h4>
                </div>
            </div>

            <template v-for="(data, i) in form.documentation"
                      :key="`data_${i}`">
                <div class="h-full col-span-2">
                    <div class="pl-14 pr-2 pt-3">
                        <InputMask
                            v-model="form.documentation[i].date"
                            mask="99.99.9999"
                            class="text-sm border-none bg-transparent shadow-none w-full"
                            placeholder="tt.mm.jjjj" />
                    </div>
                </div>

                <div class="h-full col-span-2">
                    <div class="px-2 pt-3">
                        <Dropdown
                            v-model="form.documentation[i].localization"
                            :options="[
                                'Bauch',
                                'Gesäss',
                                'Hüfte',
                                'Oberschenkel',
                                'Rücken',
                                'Arme',
                            ]"
                            class="text-sm border-none bg-transparent shadow-none w-full"></Dropdown>
                    </div>
                </div>

                <div class="h-full">
                    <div class="px-2 pt-3">
                        <Dropdown
                            v-model="form.documentation[i].applicators"
                            :options="[
                            'Straight',
                        ]"
                            class="text-sm border-none bg-transparent shadow-none w-full"></Dropdown>
                    </div>
                </div>

                <div class="h-full">
                    <div class="px-2 pt-3">
                        <InputText
                            v-model="form.documentation[i].time"
                            class="text-sm border-none bg-transparent shadow-none w-full"
                            type="number"
                            step="0.5"
                        />
                    </div>
                </div>

                <div class="col-span-3 h-full">
                    <div class="pl-2 pr-14 pt-3">

                    </div>
                </div>

                <div class="col-span-9 h-full border-b border-[#C5C6CC]">
                    <div class="pl-14 pr-14 py-3 relative">
                        <Textarea
                            v-model="form.documentation[i].remarks"
                            class="w-full border border-[#C5C6CC] text-sm lato"
                            placeholder="Bemerkung / Nachbehandlung"
                        ></Textarea>

                        <i
                            class="pi pi-minus text-[#C5C6CC] cursor-pointer absolute bottom-2.5 right-[18px]"
                            @click="form.documentation.splice(i, 1)"></i>
                    </div>
                </div>
            </template>
        </div>

        <div class="px-14">
            <div class="flex justify-between items-center">
                <a href="#"
                   @click.prevent="addMore">
                    <i class="pi pi-plus"></i>
                    hinzufügen
                </a>

                <Button
                    @click="submit"
                    :loading="submitting"
                    label="speichern"
                    class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                />
            </div>
        </div>

        <div class="px-14 pb-7">

        </div>
    </Layout>
</template>
